
import React, { useRef, useState, useEffect } from "react";
import { TextField, InputAdornment } from '@mui/material';

const MuiTimerTextField = (props) => {
  const { defaultLabel, defaultValue, inputAdornment, fieldName, onChange, autoFocus, helperText, 
    errorHelperText, disabled = false, required, position = "end", onClick } = props;
  const [value, setValue] = useState(defaultValue || "");
  const [touched, setTouched] = useState(false); // Track if the field has been focused
  const textFieldRef = useRef(null);
  useEffect(() => {
    if (defaultValue) {
      setValue(defaultValue);
    } else {
      setValue("");
    }
  }, [defaultValue]);

  useEffect(() => {
    if (autoFocus && textFieldRef.current) {
      textFieldRef.current.focus();
    }
  }, [autoFocus]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && textFieldRef.current) {
      textFieldRef.current.focus();
    }
  };

  const handleChange = (e) => {
    setValue(e.target.value);
    if (onChange) {
      onChange(fieldName, e.target.value);
    }
  };

  const handleBlur = () => {
    setTouched(true); // Mark the field as touched when it loses focus
  };

  const showError = required && touched && !value; // Show error if required, touched, and empty

  return (
      <TextField
        inputRef={textFieldRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9,.]*"
        variant="outlined"
        label={defaultLabel}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyPress}
        onBlur={handleBlur} // Add onBlur handler
        error={showError || !!errorHelperText} // Use showError or existing errorHelperText
        helperText={showError ? 'Dette feltet er påkrevd' : (errorHelperText ? errorHelperText : (helperText ? helperText : ' '))} // Conditional helper text
        fullWidth
        disabled={disabled}
        required={required} // Let Material UI handle the asterisk
        InputLabelProps={{ shrink: value !== null && value !== "" ? true : false }}
        inputProps={{
          inputMode: 'numeric',
          pattern: '[0-9]*',
          maxLength: 2
        }}
        onClick={onClick}
        InputProps={{
          [`${position}Adornment`]: (
            <InputAdornment
              position={position}
              sx={{
                color: disabled ? '#B0B0B0' : '#6F7979',
                fontFamily: 'Inter',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: '400',
                lineHeight: '24px',
                letterSpacing: '0.5px'
              }}
            >
              {inputAdornment}
            </InputAdornment>
          )
        }}
        sx={{
          width: 115,
          '& .MuiInputBase-root': {
            backgroundColor: '#E0E3E3',
            borderRadius: '8px',
            transition: 'all 0.3s ease',
            color: '#3F4949',
            fontFamily: 'Inter'
          },
          '& .MuiInputBase-root.Mui-focused': {
            backgroundColor: '#6FF6FD',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none', // default: no border
          },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: '2px solid #00696D', // on focus
          },
          '& .MuiInputBase-input': {
            textAlign: 'center',
            color: '#002021',
            fontSize: '45px',
            fontFamily: 'Inter',
            fontWeight: 400,
            lineHeight: '52px',
            wordWrap: 'break-word',
          },
          '& .MuiTypography-root': {
            color: disabled ? '#B0B0B0' : '#6F7979',
            fontFamily: 'Inter',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#00696D',
            fontFamily: 'Inter',
          },
          '& .MuiInputLabel-root.Mui-disabled': {
            color: '#B0B0B0', // example: gray out label when disabled
          },
          '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            maxWidth: 'calc(100% - 50px)',
          },
          '& .MuiInputBase-root.Mui-disabled': {
            backgroundColor: '#e1e1e1', // example: light background when disabled
            color: '#B0B0B0',           // example: gray out input text
          },
        }}
      />
  );
};

export default MuiTimerTextField;